import { useSyncExternalStore } from 'react';

/**
 * KOZOS ORA.
 *
 * ─── Miert kell? ────────────────────────────────────────────────────────────
 *
 * A felulet egy resze nem adattol fugg, hanem az IDOTOL: a "Munkaidon kivul"
 * jelzes, a fokusz mod vege, egy lejaro kezi statusz, a napvalaszto "Ma" es
 * "Tegnap" felirata. Ezeket a React csak akkor szamolja ujra, ha valamitol
 * ujrarajzolodik a komponens - es egy nyitva hagyott ablakban orakig nincs
 * ilyen ok.
 *
 * Igy allt elo az a tunet, hogy valaki reggel kilencig "munkaidon kivul" volt
 * a sajat kepernyojen, es egy F5 kellett hozza, hogy elmuljon. Telepitett
 * alkalmazasban (PWA) ez kulonosen rossz: ott nincs is cimsor, amivel ujra
 * lehetne tolteni - az ember egyszeruen rosszul latja magat.
 *
 * ─── Miert egyetlen ora? ────────────────────────────────────────────────────
 *
 * Mert kulonben minden nevsor-sor sajat `setInterval`-t inditana: szaz ember
 * listajanal szaz idozito ketyegne ugyanarra a percre. Itt EGY idozito van, a
 * komponensek pedig feliratkoznak ra.
 *
 * Az ertek percre kerekitve all elo, tehat ket ketyeges kozott VALTOZATLAN -
 * ez a `useSyncExternalStore` feltetele, es ettol nem rajzolodik ujra semmi
 * feleslegesen.
 */

const MINUTE_MS = 60_000;

const toMinute = (ms: number): number => Math.floor(ms / MINUTE_MS) * MINUTE_MS;

let current = toMinute(Date.now());
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;

function publish(): void {
  const next = toMinute(Date.now());
  if (next === current) return;
  current = next;
  for (const listener of listeners) listener();
}

/**
 * A KOVETKEZO percforduloig varunk, nem hatvan masodpercet.
 *
 * Igy a valtas ott tortenik, ahol az ember is varja (9:00-kor, nem 9:00:37-kor),
 * es az idozito nem csuszik el orak alatt.
 */
function schedule(): void {
  const delay = MINUTE_MS - (Date.now() % MINUTE_MS) + 50;
  timer = setTimeout(() => {
    publish();
    schedule();
  }, delay);
}

/**
 * Alvasbol ebredve AZONNAL ujraszamolunk.
 *
 * A hatterbe kerult lapon a bongeszo visszafogja az idozitoket - egy
 * telepitett alkalmazas akar orakig sem ketyeg. Visszateres utan nem varunk
 * egy ujabb percfordulot: az elso pillanat a fontos, amikor a felhasznalo
 * megint odanez.
 */
function onVisible(): void {
  if (document.visibilityState === 'visible') publish();
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) {
    /*
      Az elso feliratkozonak FRISS erteket adunk.

      Az idozito csak akkor jar, amig van kinek; ket feliratkozas kozott tehat
      az ertek befagy. Aki ilyenkor jon, kulonben a regi percet latna, amig le
      nem telik egy ujabb - eppen azt a kesest hozna vissza, ami ellen ez az
      egesz keszult. (A React a feliratkozas utan ujra beolvassa az erteket,
      ezert ez nem okoz elcsuszast.)
    */
    current = toMinute(Date.now());
    schedule();
    document.addEventListener('visibilitychange', onVisible);
  }
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    if (timer) clearTimeout(timer);
    timer = null;
    document.removeEventListener('visibilitychange', onVisible);
  };
}

const snapshot = (): number => current;

/**
 * A jelenlegi perc kezdete, ezredmasodpercben.
 *
 * Percenkent egyszer valtozik - aki ezt olvassa, percenkent egyszer
 * rajzolodik ujra. Idofuggo megjeleniteshez hasznald; mas celra ne, mert
 * felesleges ujrarajzolast hoz magaval.
 */
export function useMinute(): number {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
